import { SchmancyElement } from '@schmancy/mixins/index'
import { tooltip } from '@schmancy/primitives/tooltip/tooltip.directive'
import { css, html, LitElement, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { BehaviorSubject, fromEvent, merge, Observable, takeUntil } from 'rxjs'
import { delay, distinctUntilChanged, tap } from 'rxjs/operators'
import '../icons/icon'

export type NavigationRailItemClickEvent = CustomEvent<{
	icon: string
	label: string
	value: string
	active: boolean
}>

/**
 * A single destination/action within a navigation rail.
 *
 * Two anatomies, both from M3, and which one an item renders is fixed by
 * whatever mounts it rather than by any state that moves under the user: an
 * 80dp column stacks a centred label under the icon with the indicator
 * hugging the icon alone (the default), and a cascade row lays icon then
 * label out in a row, left-aligned, indicator hugging the row (`expanded`).
 * A mount never switches between them, so no destination changes shape while
 * it is being read. `navigation-rail-sheet.ts` sets `expanded` for every row
 * of a cascade it draws, in the puck's overlay and in a cascading rail's own
 * column alike; the stacked form is what the rail's own 80dp column and a
 * consumer mounting a bare item get.
 *
 * The container is the `link` a screen reader or `getByRole('link', { name })`
 * lands on: named by the consumer's `aria-label`, or else the label even while
 * the rail hides it, `aria-current="page"` while active, and carrying
 * `aria-disabled` and `aria-expanded`. The host carries none of that, so
 * nothing is announced twice. Slotted straight into a rail's column it is the
 * unnamed `listitem` that column's `role="list"` owns, as an `<li>` holds its
 * `<a>`; anywhere else — a cascade row, which wraps it in a listitem of its
 * own, or a bare mount — it has no role at all.
 * @see https://m3.material.io/components/navigation-rail/overview
 *
 * @element schmancy-navigation-rail-item
 * @slot icon - Navigation item icon (e.g., schmancy-icon)
 * @slot badge - Custom badge content
 *
 * @fires navigate - When the item is clicked
 *
 * @csspart container - The item's full-width click target, and the link that receives focus
 * @csspart indicator - The active indicator — a pill hugging the icon alone,
 *   or icon + label when `expanded`; the hover/pressed surface too
 * @csspart icon - The icon container
 * @csspart label - The label text
 * @csspart badge - The badge element
 *
 * @example
 * <!-- A slotted icon overrides the `icon` attribute -->
 * <schmancy-navigation-rail-item label="Dashboard">
 *   <schmancy-icon slot="icon">dashboard</schmancy-icon>
 * </schmancy-navigation-rail-item>
 */
@customElement('schmancy-navigation-rail-item')
export class SchmancyNavigationRailItem extends SchmancyElement {
	// Focus lands on the container link, which paints its own ring; the host only
	// holds the tabindex that decides whether that link is a Tab stop at all.
	static styles = [
		css`
			:host {
				outline: none;
			}
		`,
	]

	// A negative tabindex on the host takes its whole shadow tree out of the Tab
	// sequence, so the rail's roving tabindex keeps working on the host while
	// focus itself — from a Tab, a click, or the rail's arrow keys calling
	// `focus()` — is handed to the link that carries the role.
	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	}

	private hovering$ = new BehaviorSubject<boolean>(false)
	private pressing$ = new BehaviorSubject<boolean>(false)
	private active$ = new BehaviorSubject<boolean>(false)

	/** Material Symbols icon name. */
	@property({ type: String })
	icon = ''

	@property({ type: String })
	label = ''

	/** Routing value, included in the navigate event detail. */
	@property({ type: String })
	value = ''

	@property({ type: Boolean, reflect: true })
	get active() {
		return this.active$.value
	}
	set active(value: boolean) {
		this.active$.next(value)
	}

	/** Alias for `active`. */
	@property({ type: Boolean, reflect: true })
	get selected() {
		return this.active
	}
	set selected(value: boolean) {
		this.active = value
	}

	/**
	 * The row anatomy — icon then label, left-aligned, indicator hugging the
	 * whole row — instead of the 80dp column's stacked one. Set by whatever
	 * mounts the item, never toggled while it is on screen: the rail's own
	 * column leaves it off, the puck's drill sheet turns it on for every row
	 * it projects.
	 * @see https://m3.material.io/components/navigation-rail/specs
	 */
	@property({ type: Boolean, reflect: true })
	expanded = false

	/**
	 * Whether the label is rendered at all. The rail writes it from its own
	 * `labelVisibility` for the column it draws itself; every other mount — the
	 * cascade's rows, the puck's drill rows, a bare consumer-mounted item —
	 * leaves it on, so an icon-only row is something a rail asks for and never
	 * something an item defaults to.
	 *
	 * A hidden label is exactly where the tooltip earns its keep: it is the
	 * only thing left carrying the destination's name (see the `tooltip`
	 * directive at the end of `render()`).
	 */
	@property({ type: Boolean, attribute: 'show-label' })
	showLabel = true

	@property({ type: String })
	badge = ''

	/** @deprecated Counts render as ordinary text; this styling option has no effect. */
	@property({ type: String })
	badgeVariant: 'error' | 'primary' | 'secondary' = 'error'

	/**
	 * Set by the parent rail, not typically consumer-controlled — the label
	 * this item would show as a tooltip, and empty when the rail's own
	 * `showTooltips` is off. It is the STRING, never the decision: whether a
	 * tooltip appears is `labelClipped` below. Drives the `tooltip` directive,
	 * never the native `title` attribute, which would fire the browser's own
	 * tooltip on top of this one.
	 */
	@property({ type: String, attribute: 'tooltip-text' })
	tooltipText = ''

	/**
	 * How many lines the stacked label may take before it is clipped. Set by
	 * the parent rail, never per item — rows of one column all reserve the
	 * same height, so a rail where one destination wraps does not sit on an
	 * uneven grid. `1` is the M3 default; `2` is M3's own maximum for the
	 * stacked anatomy, and it roughly doubles what an 80dp column can hold
	 * without widening the column or renaming the destination. Ignored in the
	 * `expanded` row anatomy, where the label stands beside its icon in a
	 * 208px column — a 148px label measure, derived at the active weight — and
	 * has room on one line.
	 */
	@property({ type: Number, attribute: 'label-lines' })
	labelLines: 1 | 2 = 1

	@property({ type: Boolean, reflect: true })
	disabled = false

	@property({ type: Boolean, reflect: true })
	nested = false

	@property({ type: Boolean, reflect: true })
	group = false

	#givenName: string | null = null

	/**
	 * The consumer's own name for this destination, given as `aria-label` on the
	 * host. It lands on the link and the host's copy is taken away (`willUpdate`);
	 * the setter ignores that removal's null so it does not read back as the
	 * consumer clearing the name.
	 */
	@property({ attribute: 'aria-label' })
	get accessibleName(): string | null {
		return this.#givenName
	}
	set accessibleName(value: string | null) {
		if (value === null) return
		this.#givenName = value
		this.requestUpdate('accessibleName')
	}

	/**
	 * Set by a mount — a cascade row that owns a level. Rendered on the link, the
	 * element that can report it; the host itself carries no state.
	 */
	@property({ attribute: 'aria-expanded' })
	public override ariaExpanded: 'true' | 'false' | null = null

	@state()
	private showRipple = false

	/**
	 * Whether the label, while shown, is clipped by the box this item was
	 * given — on either axis, because the two anatomies clip on different
	 * ones: a one-line label is `whitespace-nowrap` and runs out of width,
	 * a clamped one wraps and runs out of lines. Only the element's own
	 * overflow can tell a chopped label from a fitted one; they look alike at
	 * a glance.
	 *
	 * It is half the tooltip verdict — the half that applies while the label
	 * is on screen at all; `showLabel` above is the other.
	 *
	 * Read from a ResizeObserver rather than from `updated()`, because the
	 * observation lands after layout and outside this element's own update
	 * cycle — writing it during `updated()` would schedule a second render of
	 * every item on every render of every item, which is the shape this
	 * component is being kept clear of.
	 */
	@state()
	private labelClipped = false

	private labelResize?: ResizeObserver

	/** The label node the observer currently holds — `when(this.label, …)` drops
	 *  it entirely for a label-less item, so the node can come and go. */
	private observedLabel: HTMLElement | null = null

	connectedCallback() {
		super.connectedCallback()

		new Observable<boolean>(subscriber => {
			this.labelResize = new ResizeObserver(() => {
				const label = this.renderRoot.querySelector('[part="label"]')
				subscriber.next(
					label instanceof HTMLElement &&
						(label.scrollWidth > label.clientWidth || label.scrollHeight > label.clientHeight),
				)
			})
			return () => {
				this.labelResize?.disconnect()
				this.labelResize = undefined
				this.observedLabel = null
			}
		})
			.pipe(
				distinctUntilChanged(),
				tap(clipped => {
					this.labelClipped = clipped
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		merge(
			fromEvent(this, 'click').pipe(tap(event => this.handleClick(event))),
			fromEvent<KeyboardEvent>(this, 'keydown').pipe(tap(event => this.handleKeyDown(event))),
			fromEvent(this, 'mouseenter').pipe(tap(() => this.hovering$.next(true))),
			fromEvent(this, 'mouseleave').pipe(tap(() => this.hovering$.next(false))),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()

		merge(
			fromEvent(this, 'mousedown').pipe(tap(() => this.pressing$.next(true))),
			fromEvent(this, 'mouseup').pipe(tap(() => this.pressing$.next(false))),
			fromEvent(this, 'mouseleave').pipe(tap(() => this.pressing$.next(false))),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()

		this.pressing$
			.pipe(
				tap(pressing => {
					if (pressing && !this.disabled) {
						this.showRipple = true
					}
				}),
				delay(600), // M3 standard ripple duration
				tap(() => (this.showRipple = false)),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		this.active$
			.pipe(
				distinctUntilChanged(),
				tap(isActive => {
					this.requestUpdate()
					this.setAttribute('tabindex', isActive ? '0' : '-1')
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		// Only a rail's own column puts this host inside a role="list". Anywhere
		// else a listitem is an orphan, or — in a cascade row — nested inside the
		// row's own listitem.
		if (!this.hasAttribute('role') && this.parentElement?.localName === 'schmancy-navigation-rail') {
			this.setAttribute('role', 'listitem')
		}
		if (!this.hasAttribute('tabindex')) {
			this.setAttribute('tabindex', this.active ? '0' : '-1')
		}
	}

	protected willUpdate(changedProperties: PropertyValues) {
		super.willUpdate(changedProperties)
		// A name on the host and on the link inside it is announced twice; the link keeps it.
		this.removeAttribute('aria-label')
	}

	updated(changedProperties: PropertyValues) {
		super.updated(changedProperties)

		// Re-target the clipping observer only when the label node itself
		// changed. `observe()` delivers an initial observation of its own, so
		// re-observing on every render would hand the observer a fresh
		// notification each time its own verdict caused a re-render — the loop
		// the browser reports as "ResizeObserver loop completed with undelivered
		// notifications". A same-node render leaves the observation standing;
		// every genuine size change still arrives through it.
		const label = this.renderRoot.querySelector('[part="label"]')
		const next = label instanceof HTMLElement ? label : null
		if (next !== this.observedLabel && this.labelResize) {
			if (this.observedLabel) this.labelResize.unobserve(this.observedLabel)
			if (next) this.labelResize.observe(next)
			this.observedLabel = next
		}
	}

	private handleClick(event: Event) {
		if (this.disabled) {
			event.preventDefault()
			event.stopPropagation()
			return
		}

		this.dispatchEvent(
			new CustomEvent('navigate', {
				detail: this.value || this.label,
				bubbles: true,
				composed: true,
			}),
		)

		// Active state isn't set here — the parent rail confirms and updates it via activeIndex.
	}

	private handleKeyDown(event: KeyboardEvent) {
		if (this.disabled) return

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			this.click()
		}
	}

	protected render() {
		const hasCustomIcon = !!this.querySelector('[slot="icon"]')
		const hasCustomBadge = this.querySelector('[slot="badge"]')
		// Without an icon, the label occupies the indicator so hover feedback
		// stays attached to visible content.
		const hasIcon = hasCustomIcon || !!this.icon

		const containerClasses = this.classMap({
			'relative select-none cursor-pointer box-border': true,
			// M3: "the target area should always span the full width of the nav
			// rail, even if the item container hugs its contents" — this container
			// is the click TARGET; the visible pill is `indicatorClasses` below.
			'w-full': true,
			// NOT `transition-all`: the focus ring below is `outline-width` and
			// `outline-offset`, both animatable, so `all` made the ring GROW IN
			// over 150ms instead of landing with the focus. Measured — read the
			// moment Tab lands, the width came back 3px (the pre-focus `medium`
			// initial) or 1px and the offset 1px, settling on 2px/2px only after
			// the transition ran. A focus indicator that arrives a beat late is
			// the one state that may never be animated in. What the container
			// actually changes is its text colour (active) and its opacity
			// (disabled); the pill's own hover and pressed feedback is the
			// indicator's `transition-colors` below.
			'transition-[color,opacity] duration-150 ease-out': true, // M3 emphasized motion
			// `outline-solid`, not a bare `outline`, matching <schmancy-button>: the
			// bare utility emits its own `outline-width: 1px`, which `outline-2`
			// then beats on source order — a dead declaration that reads like a
			// cascade fight and is not one.
			'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default': true,

			// Both anatomies are flex, centred on the cross axis — hoisted out of
			// the two conditional groups below so the two never re-declare the same
			// literal token under opposite conditions: `classMap()` splits each
			// multi-token key into one boolean per token and the LAST group
			// processed wins that token regardless of which group's own condition
			// is true, so an `items-center` present in both groups collapses to
			// whichever group is declared last, not whichever is actually active.
			'flex items-center': true,

			// Icon and label carry their own active color overrides; container text
			// sets the default for any non-overridden text children.
			'text-surface-onVariant': !this.active,
			'text-primary-default': this.active,

			// 80dp column — icon above a centred label. min-h-14 is M3's 56dp
			// minimum hit target AND what holds the column's rhythm even when
			// the rail has hidden the labels: without it a labelled row stands
			// 60px tall beside a 40px icon-only one, and `labelVisibility` turns
			// a list of destinations into a ragged one. The icon stays pinned to
			// the row top (a column's default main-axis start), so its vertical
			// centre is invariant across both states.
			'min-h-14 flex-col gap-1 py-1': !this.expanded,
			// Drill-sheet row — icon then label, 40px tall.
			'flex-row gap-2 min-h-10': this.expanded,

			'pointer-events-none opacity-[var(--schmancy-sys-state-opacity-disabled,0.38)]': this.disabled,
			'min-h-12 pl-8': this.nested,

			'mb-2 after:absolute after:bottom-[-4px] after:left-3 after:right-3 after:h-px after:bg-outline-variant after:opacity-12':
				this.group,
		})

		// Selection uses foreground and label weight without a persistent fill.
		// The content-hugging indicator carries hover and pressed feedback.
		const indicatorClasses = this.classMap({
			'inline-flex items-center min-w-0': true,
			'rounded-full relative overflow-hidden': true,
			'transition-colors duration-150 ease-out': true,
			'hover:bg-surface-containerHighest': !this.active,
			// M3's 32dp-tall collapsed indicator, 56dp wide while it is hugging
			// the icon alone.
			'h-8 justify-center': !this.expanded,
			'w-14': !this.expanded && hasIcon,
			// 40px row hugging icon + label.
			'h-10 gap-2': this.expanded,
			// 6px is the row pill's floor rather than its taste: `rounded-full` at
			// h-10 gives it a 20px corner, which has already curved
			// 20 − √(20² − 12²) = 4px inward by the icon's own top edge (24px
			// tall, ±12px off centre) — 4 makes them touch, 6 clears by 2. The 4px
			// it hands back is what lands the column's measure on w-52
			// (navigation-rail.ts's render()); the icon→label gap stays at M3's
			// 8dp, whose own 2px is not worth spending the spec on. The stacked
			// anatomy's label-only capsule is a different box on a different
			// column and keeps its 8.
			'px-1.5': this.expanded,
			'px-2': !this.expanded && !hasIcon,
			'max-w-full': !this.expanded && !hasIcon,
		})

		const iconContainerClasses = this.classMap({
			'flex items-center justify-center shrink-0': true,
		})

		const iconClasses = this.classMap({
			'relative z-100 leading-none': true,
			'text-2xl': !this.nested,
			'text-xl': this.nested,
			'text-primary-default': this.active,
		})

		// M3 allows the stacked label two lines, and two lines is what keeps a
		// destination's own name intact in an 80dp column: the alternatives are
		// widening the column for every screen or renaming the destination to fit
		// a box, and a name the product already uses everywhere else outranks
		// both. The row anatomy keeps one line — it has a 148px label measure.
		const clamped = this.labelLines === 2 && !this.expanded
		const labelClasses = this.classMap({
			// Whichever anatomy, the label overflows its own box and nothing else,
			// so `labelClipped` above stays answerable: one line runs out of width,
			// two run out of lines, and the tooltip carrying the full string
			// appears for either.
			'overflow-hidden min-w-0': true,
			'text-ellipsis whitespace-nowrap': !clamped,
			// A reserved two-line box, so one wrapping destination does not stand
			// taller than the rows around it. A word too long for one line — a
			// German compound like `Einstellungen` — breaks at a syllable with a
			// hyphen, in the language the document's `lang` names; `break-words`
			// is left for a word the language cannot hyphenate.
			'line-clamp-2 hyphens-auto break-words min-h-8': clamped,
			hidden: !this.showLabel,
			'text-primary-default': this.active,
			'font-medium': this.active,
			// M3 label-medium in the stacked anatomy, body-md in the row one.
			'text-xs': !this.expanded,
			'text-sm leading-5': this.expanded,
			// Centred beneath the icon across the whole column — and left-aligned
			// wherever it is inside the pill instead, beside an icon or standing
			// in for one.
			'w-full text-center': !this.expanded && hasIcon,
			'text-left': this.expanded || !hasIcon,
		})

		const badgeClasses = 'text-xs font-medium shrink-0 text-surface-on-variant'

		const rippleClasses = this.classMap({
			'absolute inset-0 overflow-hidden z-0': true,
			'rounded-full': true,
			'before:content-[""] before:absolute before:top-1/2 before:left-1/2': true,
			'before:w-0 before:h-0 before:rounded-full': true,
			'before:bg-current before:opacity-0': true,
			'before:-translate-x-1/2 before:-translate-y-1/2': true,
			'before:transition-all before:duration-300': true,
			'before:w-[200%] before:h-[200%] before:opacity-[var(--schmancy-sys-state-opacity-pressed,0.12)]':
				this.showRipple,
		})

		const iconTemplate = when(
			hasCustomIcon,
			() => html`<slot class="relative" name="icon"></slot>`,
			() =>
				when(
					this.icon,
					() => html`
						<schmancy-icon class=${iconClasses} part="icon-text">
							${this.active ? `${this.icon}-fill` : this.icon}
						</schmancy-icon>
					`,
				),
		)

		const labelTemplate = when(this.label, () => html`<span class=${labelClasses} part="label">${this.label}</span>`)

		const badgeTemplate = when(
			this.badge,
			() => html`
				${when(
					hasCustomBadge,
					() => html`<slot name="badge"></slot>`,
					() => html`
						<span class=${badgeClasses} part="badge" aria-label="${this.badge} notifications"> ${this.badge} </span>
					`,
				)}
			`,
		)

		return html`
			<div
				class=${containerClasses}
				part="container"
				role="link"
				tabindex="0"
				aria-label=${ifDefined(this.accessibleName || this.label || undefined)}
				aria-current=${ifDefined(this.active ? 'page' : undefined)}
				aria-disabled=${ifDefined(this.disabled ? 'true' : undefined)}
				aria-expanded=${ifDefined(this.ariaExpanded ?? undefined)}
				${
					// A tooltip appears only where the label itself cannot be read:
					// hidden outright by the rail's `labelVisibility`, which is the
					// whole affordance of an icon-only column, or clipped by the box
					// this row was given — which in a cascade means the deep levels,
					// since the column is measured to hold its TOP level whole and
					// every level under it spends an indent out of that same width.
					// Repeating a label the user is already reading in full states
					// what is on screen.
					tooltip(!this.showLabel || this.labelClipped ? this.tooltipText : '', { position: 'right' })
				}
			>
				<span class=${indicatorClasses} part="indicator">
					<span class=${rippleClasses} aria-hidden="true"></span>
					<div class=${iconContainerClasses} part="icon">${iconTemplate}</div>
					${when(this.expanded || !hasIcon, () => labelTemplate)}
				</span>
				${when(!this.expanded && hasIcon, () => labelTemplate)} ${badgeTemplate}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-navigation-rail-item': SchmancyNavigationRailItem
	}
}
